/**
 * ThreadList — the full chat history sidebar (STORY-002-010).
 *
 * Replaces STORY-002-009's `ThreadListSidebar` stub. Composition (desktop
 * prototype thread rail — `docs/GardEn/src/web-screens.jsx` WebChat):
 *   - "New chat" button at the top.
 *   - Sections in priority order: Pinned, Today, Last 7 days, Older
 *     (collapsible). A thread is bucketed client-side from `last_message_at`;
 *     pinned threads are lifted into the Pinned section regardless of age (AC#1).
 *   - Each row is a `<ThreadListItem>` (title + redacted snippet + action menu).
 *   - Infinite scroll fetches the next cursor page near the bottom (AC#7).
 *   - Empty state: "Start your first chat" + a New chat button (AC#9).
 *   - Keyboard nav: ArrowUp/ArrowDown move focus between rows, Enter activates
 *     (AC#10).
 *   - Mobile (<768px): the rail collapses behind a hamburger; opening shows it
 *     as a full-height sheet over an overlay (AC#15).
 *
 * Data: TanStack Query (`useThreadList`) for the list; optimistic mutations
 * (`useRenameThread`/`usePinThread`/`useDeleteThread`) with rollback + a Sonner
 * error toast on failure (AC#2/#3/#4/#6). Active-thread + scroll state lives in
 * the Zustand `chat-store` (owned by the parent `ChatRoute`).
 *
 * No PII reaches a client log: the only message text here is the server-redacted
 * snippet; nothing is logged or sent to Sentry breadcrumbs from this component.
 */
import { Plus, PanelLeftClose, Menu as MenuIcon } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";
import { notify } from "@/lib/notify";

import { Button } from "@/components/ui/button";
import { DeleteConfirmDialog } from "@/features/chat/components/DeleteConfirmDialog";
import { RenameInline } from "@/features/chat/components/RenameInline";
import {
  ThreadListItem,
  snippetToDisplay,
} from "@/features/chat/ThreadListItem";
import { useAssistantDesign } from "@/features/chat/useDesignFrame";
import {
  useDeleteThread,
  usePinThread,
  useRenameThread,
  useThreadList,
  type ThreadListItem as ThreadRow,
} from "@/features/chat/useThreadList";

const INFINITE_SCROLL_THRESHOLD_PX = 120;

/**
 * The design's rail button (spec §4.2): transparent on the dark panel, 1px
 * `--gd-panel-border`, radius 12, `12px 16px`, 14px/600, hover moving BOTH
 * the border and the ink to `--gd-accent-on-dark`.
 *
 * A plain `<button>` rather than the shadcn `Button`: that component composes
 * its variant classes without tailwind-merge, so `variant="secondary"`'s own
 * background and text colour would survive alongside these and win or lose by
 * source order (see the `@garden/ui` Button note in the repo's frontend
 * gotchas). Two callers share this — "New chat" and the empty state's CTA —
 * which is the whole reason it is a constant.
 *
 * 12 + 12 + ~20 = 44px tall, so it clears the hit-target floor unaided.
 */
export const DESIGN_BUTTON_CLASS =
  "flex min-h-[44px] w-full items-center justify-start gap-2 rounded-[12px] " +
  "border border-[var(--gd-panel-border)] bg-transparent px-4 py-3 " +
  "text-[15px] font-semibold text-[var(--gd-panel-ink)] " +
  "transition-colors duration-150 " +
  "hover:border-[var(--gd-accent-on-dark)] hover:text-[var(--gd-accent-on-dark)] " +
  "focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-[var(--gd-accent-on-dark)]";

export interface ThreadListProps {
  activeThreadId: string | null;
  /** Start a fresh thread (clears active + streaming state). */
  onNewChat: () => void;
  /** Switch to a thread (parent handles mid-stream confirm + scroll restore). */
  onSelectThread: (threadId: string) => void;
  /**
   * Mobile-sheet open state, lifted to the parent (2026-08).
   *
   * This component used to own the state AND render its own trigger as a
   * `fixed left-3 top-3 z-30` button. That button was unreachable: the AppShell
   * header is `fixed`-adjacent chrome at `z-40` occupying y 0-56px, and the
   * trigger sat at y 12-48px underneath it, so on every phone the chat history
   * was simply unopenable. The trigger now lives in the chat header where it
   * belongs (ChatRoute), and the state comes down as a prop.
   *
   * Both are optional so the component stays usable uncontrolled — its own
   * tests mount it bare.
   */
  mobileOpen?: boolean;
  onMobileOpenChange?: (open: boolean) => void;
}

type Bucket = "pinned" | "today" | "last7" | "older";

interface Sectioned {
  pinned: ThreadRow[];
  today: ThreadRow[];
  last7: ThreadRow[];
  older: ThreadRow[];
}

/**
 * Bucket a thread by pin + activity recency relative to `now` (AC#1).
 *
 * A thread with no messages falls back to `created_at` rather than dropping
 * straight to "older". It used to go to "older" unconditionally, and "older" is
 * a COLLAPSED disclosure — so a chat the user started seconds ago was filed
 * under Older and hidden behind a closed toggle, including while it was the
 * active thread. A thread created today belongs under Today; that is what its
 * timestamp says.
 */
function bucketOf(thread: ThreadRow, now: number): Bucket {
  if (thread.is_pinned) return "pinned";
  const activity = thread.last_message_at ?? thread.created_at;
  if (activity === null) return "older";
  const ts = Date.parse(activity);
  if (Number.isNaN(ts)) return "older";
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  if (ts >= startOfToday.getTime()) return "today";
  const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
  if (ts >= sevenDaysAgo) return "last7";
  return "older";
}

function sectionThreads(threads: ThreadRow[], now: number): Sectioned {
  const out: Sectioned = { pinned: [], today: [], last7: [], older: [] };
  for (const thread of threads) out[bucketOf(thread, now)].push(thread);
  return out;
}

export function ThreadList({
  activeThreadId,
  onNewChat,
  onSelectThread,
  mobileOpen: controlledOpen,
  onMobileOpenChange,
}: ThreadListProps): ReactElement {
  const { t } = useTranslation("chat");
  const navRef = useRef<HTMLDivElement | null>(null);

  /*
   * The design's dark rail (spec §4.2) — but only inside the design's own
   * frame. Read here rather than passed down as a prop so the rail answers
   * for itself: it is rendered as `AssistantFrame`'s `sidebar`, which cannot
   * hand it a prop, and every existing test mounts it bare. Under jsdom
   * `matchMedia` is absent, so this is `false` there and those suites keep
   * asserting today's appearance untouched.
   */
  const design = useAssistantDesign();

  // Controlled when the parent passes both; uncontrolled otherwise.
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const mobileOpen = isControlled ? controlledOpen : uncontrolledOpen;
  // useCallback so the handlers below can list it as a dependency honestly
  // rather than silencing exhaustive-deps — a plain function identity changes
  // every render and would make those callbacks lie about being stable.
  const setMobileOpen = useCallback(
    (open: boolean): void => {
      if (isControlled) onMobileOpenChange?.(open);
      else setUncontrolledOpen(open);
    },
    [isControlled, onMobileOpenChange],
  );
  const [olderOpen, setOlderOpen] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useThreadList();

  const renameMutation = useRenameThread();
  const pinMutation = usePinThread();
  const deleteMutation = useDeleteThread();

  const threads = useMemo<ThreadRow[]>(
    () => (data?.pages ?? []).flatMap((page) => page.threads),
    [data],
  );

  /*
   * Hide threads that are not yet conversations.
   *
   * A thread row is created the moment "New chat" is pressed, before anything
   * is said. Such a row has no title, no snippet and no last-message time, so
   * it renders as "Без названия · черновик" — and a user who opened the app a
   * few times accumulates a rail of a dozen identical ones, which is exactly
   * what the seeded account looks like today. They are unselectable in any
   * meaningful sense: tapping one lands you in the same blank composer you
   * would get from "New chat".
   *
   * So an empty, untitled, unpinned thread is not listed. Three deliberate
   * exceptions keep this from ever hiding something the user is using:
   *   • the ACTIVE thread always shows, so a chat you just started stays
   *     visible in the rail while you are in it;
   *   • a PINNED thread always shows — pinning is an explicit "keep this";
   *   • a thread with a title always shows, because the worker titled it,
   *     which only happens once it has content.
   *
   * Nothing is deleted and no request changes: this is display only, and the
   * row reappears the instant the thread has its first message.
   */
  const visibleThreads = useMemo<ThreadRow[]>(
    () =>
      threads.filter((thread) => {
        if (thread.id === activeThreadId) return true;
        if (thread.is_pinned) return true;
        if (thread.title !== null && thread.title.length > 0) return true;
        return (
          thread.last_message_at !== null ||
          (thread.last_message_snippet !== null &&
            thread.last_message_snippet.length > 0)
        );
      }),
    [threads, activeThreadId],
  );

  // `now` recomputed per render is fine — bucketing is cheap and the boundary
  // moving by a render is imperceptible.
  const sections = useMemo(
    () => sectionThreads(visibleThreads, Date.now()),
    [visibleThreads],
  );

  // B2 (SCRUM-179 residual) — auto-expand "Older" when it is the only
  // section with rows, or when it holds the active thread. A user whose
  // every thread is >7 days old otherwise sees one collapsed "Раньше ▸"
  // toggle and zero rows — visually "my history is gone" (the reporter's own
  // post-refresh capture: a successful `threads` response, sidebar showing
  // nothing). `onlyOlderHasRows` covers that screenshot's actual moment
  // (post-refresh, before `?t=` restore lands, or a genuinely inactive
  // `/chat` with no active thread at all) — a rule keyed on the active
  // thread alone cannot reach it. `activeInOlder` covers the complementary
  // case: a `?t=`-restored old thread would otherwise be the selected row
  // the user cannot see.
  //
  // Caveats (bound the claim, not the change):
  //   - a user whose large history is entirely old now pulls EVERY page on
  //     mount — the same work a manual Older click already triggers
  //     (SCRUM-136, the effect below), but now unprompted;
  //   - `activeInOlder` can only see the active thread if it sits in an
  //     ALREADY-FETCHED page, so a `?t=`-restored thread deep in unfetched
  //     history isn't auto-revealed until the progressive pull brings it in.
  const onlyOlderHasRows =
    sections.older.length > 0 &&
    sections.pinned.length + sections.today.length + sections.last7.length ===
      0;
  const activeInOlder =
    activeThreadId !== null &&
    sections.older.some((th) => th.id === activeThreadId);
  // One-shot: never re-expand after a manual collapse, so a deliberate
  // re-collapse (or a refetch that changes the bucket composition) sticks.
  const autoExpandedOlder = useRef(false);
  useEffect(() => {
    if (autoExpandedOlder.current) return;
    if (onlyOlderHasRows || activeInOlder) {
      autoExpandedOlder.current = true;
      setOlderOpen(true);
    }
  }, [onlyOlderHasRows, activeInOlder]);

  // "Empty" means nothing worth showing — a rail holding only never-used
  // drafts is empty to the user, and the empty state ("Start your first chat")
  // is the honest thing to render there.
  const isEmpty = !isLoading && !isError && visibleThreads.length === 0;

  // SCRUM bug-wave 2026-09-02: `role="listbox"` requires an ARIA-owned
  // `option` (or `group`) descendant at all times (axe `aria-required-children`,
  // impact critical). During loading/error/empty states this pane renders
  // only a `<p>`/status `<div>` — zero rows — so the listbox role is applied
  // ONLY once there is at least one `role="option"` row to own; the loading/
  // error/empty copy itself is never a listbox option. Pre-existing (never
  // touched by this wave's cluster-B diff); scoped to the minimal fix per the
  // bug-wave's Task 3 instruction not to restructure the sidebar's ARIA tree.
  const hasVisibleRows = visibleThreads.length > 0;

  // Infinite scroll: fetch the next page when near the bottom (AC#7).
  const onScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const nearBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight <
        INFINITE_SCROLL_THRESHOLD_PX;
      if (nearBottom && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  // SCRUM-136: the "Older" section is collapsed by default and pagination is
  // otherwise scroll-only — so on a short list (which never scrolls to the
  // fetch threshold) older history beyond the first page could never load.
  // Once the user opens "Older", progressively pull the remaining pages so the
  // COMPLETE history becomes reachable. The effect re-fires after each page and
  // naturally stops when `hasNextPage` turns false.
  useEffect(() => {
    if (olderOpen && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [olderOpen, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Keyboard navigation across all rendered rows (AC#10).
  const onKeyNav = useCallback(
    (
      event: React.KeyboardEvent<HTMLButtonElement>,
      _threadId: string,
    ): void => {
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      event.preventDefault();
      const container = navRef.current;
      if (!container) return;
      const rows = Array.from(
        container.querySelectorAll<HTMLButtonElement>("[data-thread-id]"),
      );
      const idx = rows.indexOf(event.currentTarget);
      if (idx === -1) return;
      const next = event.key === "ArrowDown" ? idx + 1 : idx - 1;
      rows[Math.max(0, Math.min(rows.length - 1, next))]?.focus();
    },
    [],
  );

  const handleSelect = useCallback(
    (threadId: string) => {
      setMobileOpen(false);
      onSelectThread(threadId);
    },
    [onSelectThread, setMobileOpen],
  );

  const handleNewChat = useCallback(() => {
    setMobileOpen(false);
    onNewChat();
  }, [onNewChat, setMobileOpen]);

  /*
   * Every one of these failures is retryable with the identical payload, so the
   * toast carries the retry rather than leaving the user to re-find a row in a
   * list that may have re-sorted underneath them. `id` collapses repeats: a
   * flaky connection now updates one toast instead of stacking four.
   */
  const handleTogglePin = useCallback(
    (threadId: string, nextPinned: boolean) => {
      const run = (): void => {
        pinMutation.mutate(
          { threadId, isPinned: nextPinned },
          {
            onError: () =>
              notify.error(t("pinError"), {
                id: `thread-pin-${threadId}`,
                action: { label: t("errors.retry"), onClick: run },
              }),
          },
        );
      };
      run();
    },
    [pinMutation, t],
  );

  const handleConfirmRename = useCallback(
    (threadId: string, title: string) => {
      setRenamingId(null);
      const run = (): void => {
        renameMutation.mutate(
          { threadId, title },
          {
            onError: () =>
              notify.error(t("rename.error"), {
                id: `thread-rename-${threadId}`,
                action: { label: t("errors.retry"), onClick: run },
              }),
          },
        );
      };
      run();
    },
    [renameMutation, t],
  );

  const handleConfirmDelete = useCallback(
    (threadId: string) => {
      setDeletingId(null);
      const run = (): void => {
        deleteMutation.mutate(threadId, {
          onError: () =>
            notify.error(t("delete.error"), {
              id: `thread-delete-${threadId}`,
              action: { label: t("errors.retry"), onClick: run },
            }),
        });
      };
      run();
    },
    [deleteMutation, t],
  );

  const deletingThread =
    deletingId !== null ? threads.find((x) => x.id === deletingId) : undefined;

  const renderRow = (thread: ThreadRow): ReactElement => {
    if (renamingId === thread.id) {
      return (
        <div key={thread.id} className="px-0.5 py-0.5">
          <RenameInline
            initialTitle={thread.title ?? ""}
            onConfirm={(title) => handleConfirmRename(thread.id, title)}
            onCancel={() => setRenamingId(null)}
          />
        </div>
      );
    }
    return (
      <ThreadListItem
        key={thread.id}
        thread={thread}
        design={design}
        isActive={thread.id === activeThreadId}
        onSelect={handleSelect}
        onRename={setRenamingId}
        onTogglePin={handleTogglePin}
        onDelete={setDeletingId}
        onKeyNav={onKeyNav}
      />
    );
  };

  const listBody = (
    <>
      <div className={design ? "" : "p-3"}>
        {design ? (
          <button
            type="button"
            className={DESIGN_BUTTON_CLASS}
            onClick={handleNewChat}
            data-testid="chat-new"
          >
            <Plus className="size-4" aria-hidden="true" />
            {t("newChat")}
          </button>
        ) : (
          <Button
            type="button"
            variant="secondary"
            className="h-11 w-full justify-start gap-2"
            onClick={handleNewChat}
            data-testid="chat-new"
          >
            <Plus className="size-4" aria-hidden="true" />
            {t("newChat")}
          </Button>
        )}
      </div>

      <div
        ref={navRef}
        onScroll={onScroll}
        className={"flex-1 overflow-y-auto " + (design ? "" : "px-2 pb-3")}
        role={hasVisibleRows ? "listbox" : undefined}
        aria-label={hasVisibleRows ? t("threadList") : undefined}
        data-testid="chat-thread-listbox"
      >
        {isLoading && (
          <p
            className={
              "py-3 text-[15px]/[1.429] " +
              (design
                ? "px-3 text-[var(--gd-panel-ink-3)]"
                : "px-2 text-muted-foreground")
            }
          >
            {t("sidebar.loading")}
          </p>
        )}

        {isError && (
          <p
            role="alert"
            className={
              "py-3 text-[15px]/[1.429] " +
              (design
                ? "px-3 text-[var(--gd-accent-on-dark)]"
                : "px-2 text-destructive")
            }
            data-testid="chat-thread-load-error"
          >
            {t("sidebar.loadError")}
          </p>
        )}

        {isEmpty && (
          <div
            className={
              "flex flex-col items-start gap-3 py-6 " + (design ? "" : "px-2")
            }
            data-testid="chat-thread-empty"
          >
            {/* Explanatory line only. This block used to carry its OWN
                "New chat" button, which stacked under the rail's header
                button — same label, same action, ~4rem apart — for every
                user with no threads. The header button is always rendered,
                so it is the one users learn; the duplicate is gone and
                `empty.sidebar.cta` is now unused. */}
            <p
              className={
                "text-[15px]/[1.429] " +
                (design
                  ? "text-[var(--gd-panel-ink-3)]"
                  : "text-muted-foreground")
              }
            >
              {t("empty.sidebar.title")}
            </p>
          </div>
        )}

        {sections.pinned.length > 0 && (
          <ThreadSection title={t("sidebar.sectionPinned")} design={design}>
            {sections.pinned.map(renderRow)}
          </ThreadSection>
        )}
        {sections.today.length > 0 && (
          <ThreadSection title={t("sidebar.sectionToday")} design={design}>
            {sections.today.map(renderRow)}
          </ThreadSection>
        )}
        {sections.last7.length > 0 && (
          <ThreadSection title={t("sidebar.sectionLast7Days")} design={design}>
            {sections.last7.map(renderRow)}
          </ThreadSection>
        )}

        {sections.older.length > 0 && (
          <div className="mt-3">
            <button
              type="button"
              aria-expanded={olderOpen}
              onClick={() => setOlderOpen((v) => !v)}
              data-testid="chat-thread-older-toggle"
              className={
                "flex w-full items-center justify-between py-1.5 " +
                (design
                  ? "rounded-[8px] px-3 text-[11.5px] uppercase tracking-[0.14em] text-[var(--gd-panel-ink-4)] hover:bg-[var(--gd-panel-hover)]"
                  : "rounded-md px-2 text-[13px]/[1.333] font-semibold uppercase tracking-wide text-muted-foreground hover:bg-secondary/60")
              }
              style={design ? { fontFamily: "var(--gd-mono)" } : undefined}
            >
              <span>{t("sidebar.sectionOlder")}</span>
              <span aria-hidden="true">{olderOpen ? "▾" : "▸"}</span>
            </button>
            {olderOpen && (
              <div
                className="mt-1 grid grid-cols-1 gap-0.5"
                data-testid="chat-thread-older"
              >
                {sections.older.map(renderRow)}
              </div>
            )}
          </div>
        )}

        {isFetchingNextPage && (
          <p
            className="px-2 py-2 text-[13px]/[1.333] text-muted-foreground"
            data-testid="chat-thread-loading-more"
          >
            {t("sidebar.loading")}
          </p>
        )}
      </div>

      {deletingId !== null && (
        <DeleteConfirmDialog
          threadTitle={
            (deletingThread?.title ?? "").length > 0
              ? (deletingThread?.title as string)
              : (snippetToDisplay(
                  deletingThread?.last_message_snippet ?? null,
                  t("sidebar.snippetRedacted"),
                ) ?? t("sidebar.untitled"))
          }
          onConfirm={() => handleConfirmDelete(deletingId)}
          onCancel={() => setDeletingId(null)}
        />
      )}
    </>
  );

  return (
    <>
      {/* Mobile hamburger toggle (AC#15). Rendered ONLY when uncontrolled: a
          controlled parent (ChatRoute) puts the trigger in the chat header
          instead, because a viewport-fixed button here lands underneath the
          AppShell header and can never be pressed. */}
      {!isControlled && (
        <button
          type="button"
          aria-label={t("sidebar.open")}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen(true)}
          data-testid="chat-sidebar-toggle"
          className="fixed left-3 top-3 z-50 flex size-9 items-center justify-center rounded-md border border-border bg-card text-foreground md:hidden"
        >
          <MenuIcon className="size-4" aria-hidden="true" />
        </button>
      )}

      {/* Desktop fixed rail. 256px in both worlds; the design darkens it
          (spec §4.2) and drops the right border, which a `--gd-panel` column
          against the cream ground does not need. */}
      <aside
        className={
          "hidden w-64 shrink-0 flex-col md:flex " +
          (design
            ? "gap-5 bg-[var(--gd-panel)] px-4 py-5"
            : "border-r border-[var(--g-rule)] bg-[var(--g-surface)]")
        }
        style={design ? { fontFamily: "var(--gd-text)" } : undefined}
        aria-label={t("threadList")}
        data-testid="chat-sidebar"
        data-design={design ? "true" : undefined}
      >
        {listBody}

        {/*
         * The design's live-inventory footer (spec §4.2), BUILT AND HIDDEN.
         *
         * The design reads "Live inventory · updated 07:40". No catalogue
         * freshness timestamp exists anywhere in the API (decision D4; filed
         * in the backend capabilities spec), and a plausible-looking time is
         * exactly the fabrication this surface must never commit. So the slot
         * and its 7px `--gd-live` dot ship complete, `hidden`, with the copy
         * already translated: when the backend sends a timestamp, this loses
         * the `hidden` and gains a value, and nothing else changes.
         */}
        {design && (
          <div
            hidden
            data-testid="chat-rail-footer"
            className="mt-auto flex items-center gap-2 text-[12.5px] text-[var(--gd-panel-ink-3)]"
          >
            <span
              aria-hidden="true"
              className="size-[7px] shrink-0 rounded-full bg-[var(--gd-live)]"
            />
            <span>{t("sidebar.freshness", { time: "" })}</span>
          </div>
        )}
      </aside>

      {/* Mobile sheet (AC#15). */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setMobileOpen(false);
          }}
          data-testid="chat-sidebar-sheet-overlay"
        >
          <aside
            className="flex h-full w-72 max-w-[85vw] flex-col border-r border-border bg-card"
            aria-label={t("threadList")}
            data-testid="chat-sidebar-sheet"
          >
            <div className="flex justify-end p-2">
              <button
                type="button"
                aria-label={t("sidebar.close")}
                onClick={() => setMobileOpen(false)}
                data-testid="chat-sidebar-close"
                className="flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary"
              >
                <PanelLeftClose className="size-4" aria-hidden="true" />
              </button>
            </div>
            {listBody}
          </aside>
        </div>
      )}
    </>
  );
}

/**
 * A dated group of rows. The design draws its caption as a mono 10.5px
 * `.14em`-tracked label in `--gd-panel-ink-4` (spec §4.2) — the same buckets,
 * in the design's voice.
 *
 * `grid-cols-1` (here and on the "Older" group above) is `minmax(0, 1fr)`,
 * and it is load-bearing. With the implicit `auto` track the column grew to
 * the widest row's UNWRAPPED width (4,377px inside a 224px rail, measured on
 * QA, ui-review.md §4.2): `truncate` never engaged, titles were cut mid-letter
 * at the rail's edge, and focusing a row — any click or arrow key — scrolled
 * the listbox thousands of pixels sideways until every title had left it.
 */
function ThreadSection({
  title,
  design,
  children,
}: {
  title: string;
  design: boolean;
  children: ReactElement | ReactElement[];
}): ReactElement {
  return (
    <div className="mt-3 first:mt-1">
      <div
        className={
          design
            ? "px-3 pb-2 text-[11.5px] uppercase tracking-[0.14em] text-[var(--gd-panel-ink-4)]"
            : "px-2 pb-1.5 text-[13px]/[1.333] font-semibold uppercase tracking-wide text-muted-foreground"
        }
        style={design ? { fontFamily: "var(--gd-mono)" } : undefined}
      >
        {title}
      </div>
      <div className="grid grid-cols-1 gap-0.5">{children}</div>
    </div>
  );
}

export default ThreadList;
